import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { userSelectors } from 'reducers';
import { useCreateCommentMutation } from 'api/main';
import { parseFullName } from 'utils/helpers';
import { useMatchAtLeastOneRole } from 'utils/hooks';
import { RoleNames } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { CustomTextField, CancelButton, ActionButton } from 'components';
import { Box, Typography, IconButton } from '@mui/material';
import { PlusCircle } from 'mdi-material-ui';

const Comments = ({ subscription, disableLeftPadding, disableNewComment }) => {
    const internalUser = useSelector(userSelectors.getInternalUser);
    const [openInsertSection, setOpenInsertSection] = useState(false);
    const [insertedComment, setInsertedComment] = useState('');
    const [error, setError] = useState('');

    const userIsStandardServicer = useMatchAtLeastOneRole([RoleNames.SERVICER_STANDARD]);

    const comments = useMemo(
        () => (subscription.comments ? [...subscription.comments].reverse() : []),
        [subscription]
    );
    const [localComments, setLocalComments] = useState(comments);
    const [createComment, { isLoading, isSuccess, data: commentResponse }] =
        useCreateCommentMutation();

    const insertNewComment = useCallback(() => {
        setOpenInsertSection(true);
        setInsertedComment('');
        setError('');
    }, []);

    const handleNewCommentChange = useCallback(({ target: { value } }) => {
        setInsertedComment(value);
        setError('');
    }, []);

    const handleSubmit = useCallback(() => {
        if (insertedComment) {
            const payload = {
                author: `/api/users/${internalUser.id}`,
                description: insertedComment,
                subscription: `/api/subscriptions/${subscription?.id}`,
            };
            createComment(payload);
        } else {
            setError(texts.LAISSER_UN_COMMENTAIRE);
        }
    }, [insertedComment, subscription, internalUser, createComment]);

    useEffect(() => {
        // SUCCESS side effect for adding a comment
        if (isSuccess) {
            setLocalComments(prevState => [
                {
                    id: commentResponse.id,
                    description: commentResponse.description,
                    author: {
                        firstName: internalUser.firstName,
                        lastName: internalUser.lastName,
                    },
                    createdAt: commentResponse.createdAt,
                },
                ...prevState,
            ]);
            setOpenInsertSection(false);
        }
    }, [isSuccess, commentResponse, internalUser]);

    useEffect(() => {
        setLocalComments(comments);
    }, [comments]);

    return (
        <Box pr={4} pl={disableLeftPadding ? 0 : 4}>
            <Box display="flex" mb={1} mt={4} alignItems="center">
                <Typography color="primary" variant="body1">
                    {texts.COMMENTAIRES}
                </Typography>
                {!disableNewComment && !userIsStandardServicer && (
                    <IconButton
                        size="small"
                        color="primary"
                        sx={{ marginLeft: '15px' }}
                        onClick={insertNewComment}
                    >
                        <PlusCircle />
                    </IconButton>
                )}
            </Box>
            {localComments?.map(comment => (
                <Typography key={comment.id} variant="body2" gutterBottom>{`${
                    comment.description.charAt(0).toUpperCase() + comment.description.substring(1)
                } | ${parseFullName(comment.author)} | ${moment(comment.createdAt).format(
                    'DD.MM.YYYY'
                )}`}</Typography>
            ))}
            {openInsertSection && (
                <React.Fragment>
                    <CustomTextField
                        multiline
                        rows={4}
                        value={insertedComment}
                        onChange={handleNewCommentChange}
                        error={error}
                    />
                    <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                        <ActionButton
                            size="small"
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isLoading}
                            loading={isLoading}
                        >
                            {texts.SAVE}
                        </ActionButton>
                        <CancelButton
                            disabled={isLoading}
                            size="small"
                            variant="contained"
                            onClick={() => setOpenInsertSection(false)}
                        >
                            {texts.CANCEL}
                        </CancelButton>
                    </Box>
                </React.Fragment>
            )}
        </Box>
    );
};

Comments.propTypes = {
    subscription: PropTypes.shape({
        comments: PropTypes.array,
        id: PropTypes.number,
    }),
    disableNewComment: PropTypes.bool,
    disableLeftPadding: PropTypes.bool,
};

export default Comments;
